import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { PO_PRIORITY_META } from "@/lib/poStatus";

const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const money = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lbl = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

const emptyLine = () => ({ _k: uid(), id: "", custom: false, custom_name: "", component_id: "", type_id: "", design_id: "", sku: "", quantity: "", unit: "", unit_price: "", edd: "", notes: "" });

function LineEditor({ line, components, onChange, onRemove, canRemove, idx }) {
  const comp = components.find((c) => c.id === line.component_id) || null;
  const type = (comp?.types || []).find((t) => t.id === line.type_id) || null;
  const designs = type?.designs || [];
  const total = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
  return (
    <div className="space-y-1.5 rounded-sm border border-slate-200 p-2" data-testid={`po-line-${idx}`}>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[1.2fr_1fr_1fr_36px] sm:items-center">
        {line.custom ? (
          <Input value={line.custom_name} onChange={(e) => onChange({ ...line, custom_name: e.target.value })} placeholder="Custom item name" className="rounded-sm sm:col-span-2" data-testid={`po-line-custom-name-${idx}`} />
        ) : (
          <>
            <Select value={line.component_id || undefined} onValueChange={(v) => onChange({ ...line, component_id: v, type_id: "", design_id: "" })}>
              <SelectTrigger className="rounded-sm" data-testid={`po-line-component-${idx}`}><SelectValue placeholder="Component" /></SelectTrigger>
              <SelectContent>{components.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
            <Select value={line.type_id || undefined} onValueChange={(v) => onChange({ ...line, type_id: v, design_id: "" })} disabled={!comp}>
              <SelectTrigger className="rounded-sm" data-testid={`po-line-variant-${idx}`}><SelectValue placeholder="Variant" /></SelectTrigger>
              <SelectContent>{(comp?.types || []).map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent>
            </Select>
          </>
        )}
        {!line.custom && designs.length > 0 ? (
          <Select value={line.design_id || undefined} onValueChange={(v) => onChange({ ...line, design_id: v === "__none" ? "" : v })}>
            <SelectTrigger className="rounded-sm" data-testid={`po-line-design-${idx}`}><SelectValue placeholder="Design" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">No design</SelectItem>
              {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
        ) : (
          <Input value={line.sku} onChange={(e) => onChange({ ...line, sku: e.target.value })} placeholder="SKU (optional)" className="rounded-sm" data-testid={`po-line-sku-${idx}`} />
        )}
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onRemove} disabled={!canRemove} data-testid={`po-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-[90px_110px_110px_1fr_140px]">
        <Input type="text" inputMode="decimal" value={line.quantity} onChange={(e) => onChange({ ...line, quantity: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Qty" className="rounded-sm text-right tabular-nums" data-testid={`po-line-qty-${idx}`} />
        <Select value={line.unit || undefined} onValueChange={(v) => onChange({ ...line, unit: v })}>
          <SelectTrigger className="rounded-sm text-xs" data-testid={`po-line-unit-${idx}`}><SelectValue placeholder="Unit" /></SelectTrigger>
          <SelectContent>
            {["units", "pieces", "rolls", "cases", "pallets", "kg", "lbs", "boxes"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="relative">
          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
          <Input type="text" inputMode="decimal" value={line.unit_price} onChange={(e) => onChange({ ...line, unit_price: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Unit price" className="rounded-sm pl-5 text-right tabular-nums" data-testid={`po-line-price-${idx}`} />
        </div>
        <Input value={line.notes} onChange={(e) => onChange({ ...line, notes: e.target.value })} placeholder="Line notes…" className="rounded-sm text-xs" data-testid={`po-line-notes-${idx}`} />
        <div className="flex items-center justify-end gap-2 whitespace-nowrap text-xs tabular-nums text-slate-500">
          {total > 0 && <span className="font-semibold text-emerald-700" data-testid={`po-line-total-${idx}`}>{money(total)}</span>}
          <label className="flex items-center gap-1">
            <span className="text-[10px] uppercase text-slate-400">EDD</span>
            <input type="date" value={line.edd} onChange={(e) => onChange({ ...line, edd: e.target.value })} className="rounded-sm border border-slate-200 px-1 py-0.5 text-[11px]" data-testid={`po-line-edd-${idx}`} />
          </label>
        </div>
      </div>
      {Number(line.received) > 0 && <p className="text-[10px] text-slate-400">{Number(line.received).toLocaleString()} already received — quantity cannot go below that.</p>}
    </div>
  );
}

export function PoDialog({ open, onOpenChange, initial, suppliers, components, destinations = [], onSaved }) {
  const [f, setF] = useState({});
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF({
      po_number: initial?.po_number || "", supplier_id: initial?.supplier_id || "",
      supplier_ref: initial?.supplier_ref || "", order_date: initial?.order_date || today(),
      rdd: initial?.rdd || "", esd: initial?.esd || "", edd: initial?.edd || "",
      priority: String(initial?.priority || 3), payment_terms: initial?.payment_terms || "",
      currency: initial?.currency || "USD", notes: initial?.notes || "",
      supplier_instructions: initial?.supplier_instructions || "",
      ship_method: initial?.ship_method || "", origin: initial?.origin || "",
      destination: initial?.destination || "", carrier: initial?.carrier || "",
      freight_terms: initial?.freight_terms || "", freight_cost: initial?.freight_cost ? String(initial.freight_cost) : "",
      tracking: initial?.tracking || "",
    });
    setLines(initial?.lines?.length
      ? initial.lines.map((l) => ({
          _k: uid(), id: l.id, custom: !!l.custom, custom_name: l.custom_name || "",
          component_id: l.component_id, type_id: l.type_id, design_id: l.design_id || "",
          sku: l.sku || "", quantity: String(l.quantity), unit: l.unit || "",
          unit_price: l.unit_price ? String(l.unit_price) : "", edd: l.edd || "", notes: l.notes || "",
          received: l.received,
        }))
      : [emptyLine()]);
  }, [open, initial]);

  useEffect(() => {
    if (!open || initial?.id || !f.supplier_id) return;
    const sup = suppliers.find((s) => s.id === f.supplier_id);
    if (sup) {
      const addr = sup.address || (sup.addresses || []).map((a) => a.text).find(Boolean) || "";
      setF((p) => ({ ...p, origin: p.origin || addr }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.supplier_id]);

  const valid = f.supplier_id && lines.every((l) => (l.custom ? l.custom_name.trim() : l.component_id && l.type_id) && Number(l.quantity) > 0);
  const subtotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const total = subtotal + (Number(f.freight_cost) || 0);

  const save = async (status) => {
    if (!valid) return;
    setSaving(true);
    try {
      const payload = {
        ...f, priority: Number(f.priority) || 3, freight_cost: Number(f.freight_cost) || 0,
        status: initial?.id ? (initial.status || "open") : status,
        lines: lines.map((l) => ({
          id: l.id || "", custom: !!l.custom, custom_name: l.custom_name,
          component_id: l.component_id, type_id: l.type_id, design_id: l.design_id || "",
          sku: l.sku, quantity: Number(l.quantity), unit: l.unit,
          unit_price: Number(l.unit_price) || 0, edd: l.edd, notes: l.notes,
        })),
      };
      const { data } = initial?.id
        ? await api.put(`/purchase-orders/${initial.id}`, payload)
        : await api.post("/purchase-orders", payload);
      toast.success(initial?.id ? `${data.po_number} updated` : (status === "draft" ? `${data.po_number} saved as draft` : `${data.po_number} issued — components are now On Order`));
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save purchase order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="po-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{initial?.id ? `Edit ${initial.po_number}` : "New Purchase Order"}</DialogTitle>
          <DialogDescription>Order components from a supplier. Issued POs appear as On Order until received into inventory.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="space-y-1 col-span-2">
              <Label className={lbl}>Supplier</Label>
              <Select value={f.supplier_id || undefined} onValueChange={(v) => set("supplier_id", v)}>
                <SelectTrigger className="rounded-sm" data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>PO Number</Label>
              <Input value={f.po_number || ""} onChange={(e) => set("po_number", e.target.value)} placeholder="Auto" className="rounded-sm" data-testid="po-number-input" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Supplier Ref</Label>
              <Input value={f.supplier_ref || ""} onChange={(e) => set("supplier_ref", e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="po-supplier-ref" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="space-y-1"><Label className={lbl}>Order Date</Label><Input type="date" value={f.order_date || ""} onChange={(e) => set("order_date", e.target.value)} className="rounded-sm" data-testid="po-order-date" /></div>
            <div className="space-y-1"><Label className={lbl}>Required Delivery (RDD)</Label><Input type="date" value={f.rdd || ""} onChange={(e) => set("rdd", e.target.value)} className="rounded-sm" data-testid="po-rdd" /></div>
            <div className="space-y-1"><Label className={lbl}>Expected Ship (ESD)</Label><Input type="date" value={f.esd || ""} onChange={(e) => set("esd", e.target.value)} className="rounded-sm" data-testid="po-esd" /></div>
            <div className="space-y-1"><Label className={lbl}>Expected Delivery (EDD)</Label><Input type="date" value={f.edd || ""} onChange={(e) => set("edd", e.target.value)} className="rounded-sm" data-testid="po-edd" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className={lbl}>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="rounded-sm" data-testid="po-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map((k) => (<SelectItem key={k} value={String(k)}>{PO_PRIORITY_META[k].label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className={lbl}>Payment Terms</Label><Input value={f.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. Net 30" className="rounded-sm" data-testid="po-payment-terms" /></div>
            <div className="space-y-1">
              <Label className={lbl}>Currency</Label>
              <Select value={f.currency || "USD"} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger className="rounded-sm" data-testid="po-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["USD", "EUR", "GBP", "CAD", "MXN", "CNY"].map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Pickup / Delivery</Label>
              <Select value={f.ship_method || "unset"} onValueChange={(v) => set("ship_method", v === "unset" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="po-ship-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not set</SelectItem>
                  <SelectItem value="pickup">We pick up</SelectItem>
                  <SelectItem value="delivery">Supplier delivers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className={lbl}>Line Items</Label>
              {subtotal > 0 && <span className="text-xs font-semibold tabular-nums text-emerald-700" data-testid="po-dialog-subtotal">Subtotal {money(subtotal)}{Number(f.freight_cost) > 0 ? ` · Total ${money(total)}` : ""}</span>}
            </div>
            {lines.map((l, i) => (
              <LineEditor key={l._k} line={l} components={components} idx={i}
                onChange={(nl) => setLines((p) => p.map((x) => (x._k === l._k ? { ...nl, _k: l._k, id: l.id, received: l.received } : x)))}
                onRemove={() => setLines((p) => p.filter((x) => x._k !== l._k))}
                canRemove={lines.length > 1 && !(Number(l.received) > 0)} />
            ))}
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="po-add-line">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add line
              </Button>
              <Button type="button" variant="outline" size="sm" className="rounded-sm text-slate-500" onClick={() => setLines((p) => [...p, { ...emptyLine(), custom: true }])} data-testid="po-add-custom-line">
                <Plus className="mr-1 h-3.5 w-3.5" /> Custom item
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1"><Label className={lbl}>Origin (supplier address)</Label><Textarea value={f.origin || ""} onChange={(e) => set("origin", e.target.value)} rows={2} className="rounded-sm text-xs" data-testid="po-origin" /></div>
            <div className="space-y-1">
              <Label className={lbl}>Destination (receiving location)</Label>
              {destinations.length > 0 && (
                <Select value="__pick" onValueChange={(v) => v !== "__pick" && set("destination", v)}>
                  <SelectTrigger className="h-7 rounded-sm text-xs text-slate-400" data-testid="po-destination-pick"><span>Pick a saved location…</span></SelectTrigger>
                  <SelectContent>{destinations.map((d, i) => (<SelectItem key={i} value={`${d.name}${d.address ? `\n${d.address}` : ""}`}>{d.group ? `${d.group} · ` : ""}{d.name}</SelectItem>))}</SelectContent>
                </Select>
              )}
              <Textarea value={f.destination || ""} onChange={(e) => set("destination", e.target.value)} rows={2} className="rounded-sm text-xs" data-testid="po-destination" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="space-y-1"><Label className={lbl}>Carrier</Label><Input value={f.carrier || ""} onChange={(e) => set("carrier", e.target.value)} className="rounded-sm" data-testid="po-carrier-input" /></div>
            <div className="space-y-1"><Label className={lbl}>Freight Terms</Label><Input value={f.freight_terms || ""} onChange={(e) => set("freight_terms", e.target.value)} placeholder="e.g. FOB Origin" className="rounded-sm" data-testid="po-freight-terms" /></div>
            <div className="space-y-1"><Label className={lbl}>Freight Cost</Label><Input type="text" inputMode="decimal" value={f.freight_cost || ""} onChange={(e) => set("freight_cost", e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-sm text-right tabular-nums" data-testid="po-freight-cost" /></div>
            <div className="space-y-1"><Label className={lbl}>Tracking / Ref #</Label><Input value={f.tracking || ""} onChange={(e) => set("tracking", e.target.value)} className="rounded-sm" data-testid="po-tracking-input" /></div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1"><Label className={lbl}>Internal Notes</Label><Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm" data-testid="po-notes" /></div>
            <div className="space-y-1"><Label className={lbl}>Supplier Instructions</Label><Textarea value={f.supplier_instructions || ""} onChange={(e) => set("supplier_instructions", e.target.value)} rows={2} className="rounded-sm" placeholder="Printed on the PO…" data-testid="po-supplier-instructions" /></div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            {!initial?.id && (
              <Button variant="outline" disabled={!valid || saving} onClick={() => save("draft")} className="rounded-sm border-slate-300" data-testid="po-save-draft">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save as Draft
              </Button>
            )}
            <Button disabled={!valid || saving} onClick={() => save("open")} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="po-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial?.id ? "Save Changes" : "Issue Purchase Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
